/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Adds request data to the log entries of matching requests. The gateway
 *
 * @remarks
 * always records a basic log entry for every request: method, host, path,
 * status, and latency. Each capture setting is a separate opt-in: request
 * headers, response headers, request body, response body, and query data.
 * The policy's `match` expressions select the requests. A policy without
 * `match` expressions matches every request. If more than one enabled
 * logging policy matches a request, the gateway combines their settings.
 * The gateway always redacts the `Authorization` header and configured key
 * locations before it stores headers or query data.
 */
export type LoggingPolicy = {
  /**
   * Capture request headers, the user agent, and the client IP. The user
   *
   * @remarks
   * agent and client IP are included because they identify the client.
   */
  requestHeaders?: boolean | undefined;
  /**
   * Capture response headers.
   */
  responseHeaders?: boolean | undefined;
  /**
   * Capture the request body, up to the capture limit.
   */
  requestBody?: boolean | undefined;
  /**
   * Capture the response body, up to the capture limit.
   */
  responseBody?: boolean | undefined;
  /**
   * Capture the query string and query parameters. Query data is a
   *
   * @remarks
   * separate opt-in because URLs can contain secrets, for example
   * `?api_key=...`.
   */
  query?: boolean | undefined;
};

/** @internal */
export const LoggingPolicy$inboundSchema: z.ZodType<
  LoggingPolicy,
  z.ZodTypeDef,
  unknown
> = z.object({
  requestHeaders: z.boolean().default(false),
  responseHeaders: z.boolean().default(false),
  requestBody: z.boolean().default(false),
  responseBody: z.boolean().default(false),
  query: z.boolean().default(false),
});
/** @internal */
export type LoggingPolicy$Outbound = {
  requestHeaders: boolean;
  responseHeaders: boolean;
  requestBody: boolean;
  responseBody: boolean;
  query: boolean;
};

/** @internal */
export const LoggingPolicy$outboundSchema: z.ZodType<
  LoggingPolicy$Outbound,
  z.ZodTypeDef,
  LoggingPolicy
> = z.object({
  requestHeaders: z.boolean().default(false),
  responseHeaders: z.boolean().default(false),
  requestBody: z.boolean().default(false),
  responseBody: z.boolean().default(false),
  query: z.boolean().default(false),
});

export function loggingPolicyToJSON(loggingPolicy: LoggingPolicy): string {
  return JSON.stringify(LoggingPolicy$outboundSchema.parse(loggingPolicy));
}
export function loggingPolicyFromJSON(
  jsonString: string,
): SafeParseResult<LoggingPolicy, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LoggingPolicy$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LoggingPolicy' from JSON`,
  );
}
